import React from 'react'

import {
  CommentsCommentFragment,
  CreateCommentForTaskDocument,
  TaskCommentsDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { CommentsCommentValues } from 'modules/comments/components/CommentsContainer/CommentEditor'
import PageComments from 'modules/comments/components/Wrappers/PageComments'
import { useTaskCommentReactions } from 'modules/tasks/hooks/useTaskCommentReactions'

import { TaskDetail } from '../../TaskElement/useTaskDetail'

interface Props {
  task: TaskDetail
}

export const Comments = ({ task }: Props) => {
  const [previousComments, setPreviousComments] = React.useState<CommentsCommentFragment[]>([])
  const [comments, setComments] = React.useState<CommentsCommentFragment[]>([])

  const { addReaction, deleteReaction } = useTaskCommentReactions()

  const [addComment] = useMutation(CreateCommentForTaskDocument, {
    extractorFn: (data) => data.tasksMutations.createCommentTasksTask,
  })

  const trackComment = () => {
    track(trackingEvents.projectManagement.tasks.addComment, {
      task_id: task.id,
    })
  }

  const companyId = useCurrentSession().company.id
  const { fetchMore, data, loading } = useQuery(TaskCommentsDocument, {
    variables: { after: null, id: task.id, first: 2, companyId },
    fetchPolicy: 'no-cache',
  })
  const totalCount = data?.tasks.task?.previousComments.totalCount || 0
  const after = data?.tasks.task?.previousComments.pageInfo.endCursor

  useEffect(() => {
    const lastComments = data?.tasks.task?.lastComments.nodes || []
    setPreviousComments(
      data?.tasks.task?.previousComments.nodes.filter((comment) => {
        return !lastComments?.find((taskComment) => taskComment.id === comment.id)
      }) || []
    )
    setComments(lastComments)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  const handleShowMore = async () => {
    const { data: moreData } = await fetchMore({
      variables: {
        after: after,
        id: task.id,
        first: 100,
        companyId,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previousResult

        return {
          ...previousResult,
          ...fetchMoreResult,
        }
      },
    })
    const newComments =
      moreData?.tasks.task?.previousComments.nodes.filter((comment) => {
        return (
          !previousComments.find((previousComment) => previousComment.id === comment.id) &&
          !comments?.find((taskComment) => taskComment.id === comment.id)
        )
      }) || []
    setPreviousComments([...previousComments, ...newComments])
  }

  const handleAddComment = async (variables: CommentsCommentValues) => {
    const result = await addComment({
      variables,
      refetchQueries: [],
    })

    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      trackComment()
      const newComment = handled.data.task?.commentsConnection.nodes[0]
      if (newComment) {
        setComments([...comments, newComment])
      }
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('comments.messages.errors.add'),
        time: 2000,
      })
    }
    return result
  }

  return (
    <>
      <PageComments
        loading={loading}
        previousComments={previousComments}
        comments={comments}
        totalCount={totalCount}
        onShowMore={handleShowMore}
        onAddComment={handleAddComment}
        resourceId={task.id}
        onAddReaction={(variables, parentResourceId) => {
          return addReaction(variables, parentResourceId)
        }}
        onDeleteReaction={deleteReaction}
      />
    </>
  )
}
